import { useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';

import { chatCapacityStatusAtomFamily } from '@/atoms/chatAtoms';
import {
  activeSidebarChatIdAtom,
  sidebarChatIdAtomFamily,
} from '@/atoms/chatSidebarAtoms';
import type { CapacityStatus } from '@/components/chat/types/types';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';
import { useCurrentSessionId } from '@heidi/runtime/shell';

const CAPACITY_STATES: Array<{
  value: CapacityStatus;
  label: string;
  color: string;
}> = [
  { value: 'ok', label: 'OK', color: 'bg-validation-success' },
  { value: 'warning', label: 'Warning', color: 'bg-icon-warning' },
  { value: 'critical', label: 'Critical', color: 'bg-icon-warning' },
  { value: 'exceeded', label: 'Exceeded', color: 'bg-icon-destructive' },
];

export function ChatCapacityDevSection() {
  const router = useRouter();
  const sessionId = useCurrentSessionId();
  const activeChatId = useAtomValue(activeSidebarChatIdAtom);
  const sessionChatId = useAtomValue(sidebarChatIdAtomFamily(sessionId || ''));
  const routeId = router.query.id;
  const routerChatId = Array.isArray(routeId) ? (routeId[0] ?? null) : (routeId ?? null);
  const chatId = activeChatId || sessionChatId || routerChatId;
  const setCapacityStatus = useSetAtom(chatCapacityStatusAtomFamily(chatId ?? ''));
  const currentStatus = useAtomValue(chatCapacityStatusAtomFamily(chatId ?? ''));

  return (
    <section aria-labelledby="dev-capacity-heading" className="mt-4">
      <TypographyP2SemiBold id="dev-capacity-heading" className="mb-1">
        Chat Capacity Guard
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        {chatId
          ? 'Set the capacity status on the current chat to preview banner states.'
          : 'Open a chat first to override its capacity status.'}
      </TypographyCaption>

      <div className="flex flex-wrap gap-1.5">
        {CAPACITY_STATES.map(({ value, label, color }) => {
          const active = currentStatus === value;
          return (
            <Button
              key={value}
              type="button"
              variant={active ? 'default' : 'outline'}
              size="xs"
              disabled={!chatId}
              onClick={() => setCapacityStatus(active ? null : value)}
              className="gap-1.5"
            >
              <span className={cn('size-2 rounded-full', color)} />
              {label}
            </Button>
          );
        })}
      </div>

      {chatId && currentStatus && (
        <TypographyCaption className="mt-2 text-text-secondary">
          Chat <strong>{chatId.slice(0, 8)}…</strong>: {currentStatus}
        </TypographyCaption>
      )}
    </section>
  );
}
